// Definições dos questionários de consulta da nutricionista.
// Cada tipo tem um modo de resposta próprio:
//  - 'aberto'     → perguntas com resposta livre (texto/data/número/escala 0-10)
//  - 'multipla'   → uma opção por pergunta
//  - 'escala'     → sintomas avaliados de 0 a 4, agrupados em seções (gera pontuação)
//  - 'frequencia' → porções semanais por alimento, agrupadas em seções

export const QUESTIONARIOS = {
  anamnese: {
    titulo: 'Anamnese Inicial',
    descricaoCurta: 'Perguntas iniciais sobre você e seus objetivos.',
    icone: '📝',
    modo: 'aberto',
    instrucoes: 'Responda as perguntas abaixo. Em caso de dúvida, deixe a resposta em branco ou entre em contato. É obrigatório responder às perguntas que possuem o símbolo *.',
    perguntas: [
      { label: 'Qual sua data de nascimento?', tipo: 'data', obrigatoria: true },
      { label: 'Qual sua altura?', tipo: 'texto', placeholder: 'Ex: 1,65 m', obrigatoria: true },
      { label: 'Qual seu peso?', tipo: 'texto', placeholder: 'Ex: 62 kg', obrigatoria: true },
      { label: 'Qual seu objetivo ao buscar acompanhamento nutricional?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Qual sua maior dificuldade em relação a alimentação?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Você faz atividade física? Se sim, qual?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Quem é responsável por cozinhar na sua casa?', tipo: 'texto', obrigatoria: true },
      { label: 'De 0 a 10, quanto você está disposto(a) a se comprometer com o tratamento?', tipo: 'escala10', obrigatoria: true },
    ],
  },

  anamnese_treino: {
    titulo: 'Anamnese de Treino',
    descricaoCurta: 'Rotina, experiência, limitações e estrutura disponível para seu treino.',
    icone: '🏋️',
    modo: 'aberto',
    instrucoes: 'Estas perguntas complementam sua anamnese inicial. Não vamos repetir medidas, alimentação ou informações que você já respondeu em outros questionários.',
    perguntas: [
      { label: 'Qual é sua profissão e como são seus horários de trabalho ou estudo?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Em quais horários você costuma dormir e acordar? Como avalia a qualidade do seu sono?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Onde você pretende treinar?', opcoes: ['Academia', 'Condomínio', 'Em casa', 'Ao ar livre', 'Outro local'], obrigatoria: true },
      { label: 'Quais aparelhos, pesos ou acessórios você terá disponíveis?', tipo: 'textolongo', placeholder: 'Ex.: academia completa, halteres, elásticos, banco...', obrigatoria: true },
      { label: 'Quantos dias por semana você consegue treinar?', opcoes: ['1 dia', '2 dias', '3 dias', '4 dias', '5 dias', '6 dias ou mais'], obrigatoria: true },
      { label: 'Quanto tempo você tem disponível em cada treino?', opcoes: ['Até 30 minutos', 'De 30 a 45 minutos', 'De 45 a 60 minutos', 'Mais de 60 minutos'], obrigatoria: true },
      { label: 'Quais atividades físicas você pratica atualmente e com qual frequência?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Como é sua experiência com musculação?', opcoes: ['Nunca pratiquei', 'Sou iniciante', 'Já pratiquei, mas estou retornando', 'Pratico regularmente'], obrigatoria: true },
      { label: 'Quais exercícios aeróbios você gosta ou consegue realizar?', tipo: 'textolongo', placeholder: 'Ex.: caminhada, esteira, bicicleta, escada...', obrigatoria: false },
      { label: 'Você tem ou já teve alguma lesão, fratura, cirurgia, luxação, entorse ou estiramento? Conte onde, quando e como está hoje.', tipo: 'textolongo', obrigatoria: true },
      { label: 'Sente alguma dor ou desconforto atualmente? Informe o local e quais movimentos pioram ou aliviam.', tipo: 'textolongo', obrigatoria: true },
      { label: 'Possui diagnóstico médico, restrição para exercício ou usa medicamento contínuo que a equipe precisa considerar?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Qual é seu principal objetivo com o treino?', tipo: 'textolongo', obrigatoria: true },
      { label: 'Existe alguma região do corpo que você deseja priorizar ou algum exercício que gosta, não gosta ou tem receio de fazer?', tipo: 'textolongo', obrigatoria: false },
    ],
  },

  disbiose: {
    titulo: 'Questionário de Disbiose Intestinal',
    descricaoCurta: 'Hábitos e histórico relacionados à saúde intestinal.',
    icone: '🦠',
    modo: 'multipla',
    instrucoes: 'Responda as perguntas abaixo. Importante: em casos de dúvidas, deixe a resposta em branco ou entre em contato.',
    perguntas: [
      { label: 'Você tem mais de 60 anos?', opcoes: ['Sim', 'Não'] },
      { label: 'De qual tipo de parto você nasceu?', opcoes: ['Cesárea', 'Parto normal', 'Não sei informar'] },
      { label: 'Você foi amamentado?', opcoes: ['Não', 'Sim, por menos de 6 meses', 'Sim, por 6 meses ou mais', 'Não sei informar'] },
      { label: 'Quantas vezes você consome frutas, verduras, legumes e/ou cereais integrais ao dia?', obs: 'Não considerar geleias e frutas em calda.', opcoes: ['Menos de 1x ao dia', '1 a 2x ao dia', '3 a 4x ao dia', '5x ao dia ou mais'] },
      { label: 'Quantas vezes por semana você consome preparações caseiras com adição de açúcar refinado ou adoçantes artificiais?', obs: 'Ex: Café com açúcar, pudim, brigadeiro, bolos, etc.', opcoes: ['Mais de 5x por semana', '4 a 5x por semana', '2 a 3x por semana', 'Menos de 1x por semana'] },
      { label: 'Quantas vezes por semana você consome alimentos industrializados?', obs: 'Ex: Refrigerantes, chocolates, salgadinhos, pratos prontos congelados, biscoito, sucos prontos, sorvetes embutidos, etc.', opcoes: ['Mais de 5x por semana', '4 a 5x por semana', '2 a 3x por semana', 'Menos de 1x por semana'] },
      { label: 'Consome bebida alcoólica? Em caso positivo, quantas doses por semana?', obs: 'Uma dose é equivalente a 1 lata de cerveja, 1 taça de vinho ou 1 dose de bebidas destiladas.', opcoes: ['Sim, acima de 4 doses', 'Sim, 3 a 4 doses', 'Sim, 1 a 2 doses', 'Não consumo'] },
      { label: 'Com qual frequência você pratica exercícios físicos atualmente?', obs: 'No mínimo 30 minutos de duração/dia.', opcoes: ['Não pratico', '1x por semana', '2x por semana', '3x por semana ou mais'] },
      { label: 'Como você avalia seu nível de estresse físico ou mental atual?', opcoes: ['Muito alto', 'Alto', 'Moderado', 'Baixo'] },
      { label: 'Você é fumante?', opcoes: ['Sim', 'Não'] },
      { label: 'Você utilizou antibióticos nos últimos 3 meses?', opcoes: ['Sim', 'Não'] },
      { label: 'Você utilizou protetores gástricos (ex: omeprazol), laxantes, anti-inflamatórios, corticoides e/ou metformina nos últimos 60 dias?', opcoes: ['Sim', 'Não'] },
      { label: 'Você está utilizando suplementação de prebióticos, probióticos e/ou simbióticos atualmente?', opcoes: ['Sim', 'Não'] },
      { label: 'Você tem alguma das seguintes enfermidades?', obs: 'Aterosclerose, Alergia, Ansiedade, Depressão, Dermatite atópica, Diabetes mellitus 1, Diabetes mellitus 2, Dislipidemia, Doença de Alzheimer, Doença de Parkinson, Doença inflamatória intestinal, Esteatose hepática não alcóolica, Hepatopatia alcóolica, HIV/AIDS, Infecção urogenital, Intolerância à lactose, Neoplasia maligna, Sobrepeso/Obesidade, Síndrome do intestino irritável, Helicobacter pylori, Gastrite, Doença respiratória (asma, bronquite, etc).', opcoes: ['Sim, 3 ou mais itens', 'Sim, 2 itens', 'Sim, 1 item', 'Não'] },
      { label: 'Você apresenta quadro de diarreia ou constipação atualmente?', obs: 'Diarreia = 3 evacuações líquidas ou mais/dia. Constipação = 1 evacuação ou menos a cada 3 dias.', opcoes: ['Sim', 'Não'] },
      { label: 'Você passou por cirurgia nos últimos 60 dias ou já fez cirurgia bariátrica?', opcoes: ['Sim', 'Não'] },
      { label: 'Você está fazendo quimioterapia ou radioterapia?', opcoes: ['Sim', 'Não'] },
    ],
  },

  rastreamento: {
    titulo: 'Questionário de Rastreamento Metabólico',
    descricaoCurta: '66 sintomas avaliados de 0 a 4, por sistema do corpo.',
    icone: '🩺',
    modo: 'escala',
    instrucoes: 'Preencha o questionário abaixo, de acordo com as legendas:',
    legenda: [
      { valor: 0, texto: 'nunca ou quase nunca teve o sintoma' },
      { valor: 1, texto: 'ocasionalmente teve, efeito não foi severo' },
      { valor: 2, texto: 'ocasionalmente teve, efeito foi severo' },
      { valor: 3, texto: 'frequentemente teve, efeito não foi severo' },
      { valor: 4, texto: 'frequentemente teve, efeito foi severo' },
    ],
    secoes: [
      { id: 'cabeca', titulo: 'Cabeça', itens: [
        'Dor de cabeça', 'Sensação de desmaio', 'Tonturas', 'Insônia',
      ]},
      { id: 'olhos', titulo: 'Olhos', itens: [
        'Olhos lacrimejantes ou coçando',
        'Olhos inchados, vermelhos ou com cílios colando',
        'Bolsas ou olheiras abaixo dos olhos',
        'Visão borrada ou em túnel (não incluiu miopia ou astigmatismo)',
      ]},
      { id: 'ouvidos', titulo: 'Ouvidos', itens: [
        'Coceira nos ouvidos', 'Dores de ouvido, infecções auditivas',
        'Retirada de fluido purulento de ouvido', 'Zunido, perda de audição',
      ]},
      { id: 'nariz', titulo: 'Nariz', itens: [
        'Nariz entupido', 'Problemas de seios nasais (Sinusite)',
        'Corrimento nasal, espirros, lacrimejamento e coceira dos olhos (juntos)',
        'Ataques de espirros', 'Excessiva formação de muco',
      ]},
      { id: 'boca_garganta', titulo: 'Boca e Garganta', itens: [
        'Tosse crônica', 'Frequente necessidade de limpar a garganta',
        'Dor de garganta, rouquidão ou perda de voz',
        'Língua, gengivas ou lábios inchados / descoloridos', 'Aftas',
      ]},
      { id: 'pele', titulo: 'Pele', itens: [
        'Acne', 'Feridas que coçam, erupções, pele seca', 'Perda de cabelo',
        'Vermelhidão na pele, calorões', 'Suor excessivo',
      ]},
      { id: 'coracao', titulo: 'Coração', itens: [
        'Batimentos cardíacos irregulares ou falhando',
        'Batimentos cardíacos rápidos demais', 'Dor no peito',
      ]},
      { id: 'pulmoes', titulo: 'Pulmões', itens: [
        'Congestão no peito', 'Asma, bronquite', 'Pouco fôlego', 'Dificuldade para respirar',
      ]},
      { id: 'trato_digestivo', titulo: 'Trato digestivo', itens: [
        'Náuseas, vômito', 'Diarreia', 'Constipação / prisão de ventre',
        'Sente-se inchado / com abdômen distendido', 'Arrotos e/ou gases intestinais',
        'Azia/Pirose', 'Dor estomacal / intestinal',
      ]},
      { id: 'articulacoes_musculos', titulo: 'Articulações / Músculos', itens: [
        'Dores articulares', 'Artrite / artrose', 'Rigidez ou limitação dos movimentos',
        'Dores musculares', 'Sensação de fraqueza ou cansaço',
      ]},
      { id: 'energia_atividade', titulo: 'Energia / Atividade', itens: [
        'Fadiga, moleza', 'Apatia, letargia', 'Hiperatividade', 'Dificuldade em descansar, relaxar',
      ]},
      { id: 'mente', titulo: 'Mente', itens: [
        'Memória ruim', 'Confusão mental, compreensão ruim', 'Concentração ruim',
        'Fraca coordenação motora', 'Dificuldade de tomar decisões',
        'Fala com repetição de sons ou palavras, com várias pausas involuntárias',
        'Pronuncia palavras de forma indistinta, confusa', 'Problemas de aprendizagem',
      ]},
      { id: 'emocoes', titulo: 'Emoções', itens: [
        'Mudanças de humor / mau humor matinal', 'Ansiedade, medo, nervosismo',
        'Raiva, irritabilidade, agressividade', 'Depressão',
      ]},
      { id: 'outros', titulo: 'Outros', itens: [
        'Frequentemente doente', 'Frequentemente ou urgente vontade de urinar',
        'Coceira genital ou corrimento', 'Edema / Inchaço - Pés / Pernas / Mãos',
      ]},
    ],
  },

  qfa: {
    titulo: 'Questionário de Frequência Alimentar',
    descricaoCurta: 'Quantas porções você consome por semana de cada alimento.',
    icone: '🍽️',
    modo: 'frequencia',
    instrucoes: 'Marque a quantidade de porções que você consome SEMANALMENTE, em média, de cada categoria de alimentos abaixo (atente-se à quantidade de cada um deles):',
    opcoes: ['0', '1-3', '4-6', '7-9', '10 ou +'],
    secoes: [
      { id: 'leites', titulo: 'Leites e Derivados', itens: [
        'Leite (copo de requeijão)',
        'Iogurte natural (copo de requeijão)',
        'Queijos (1/2 fatia)',
        'Requeijão / Crême de ricota etc (1,5 colher de sopa)',
      ]},
      { id: 'carnes_ovos', titulo: 'Carnes e Ovos', itens: [
        'Ovo cozido / mexido (2 unidades)',
        'Carnes vermelhas (1 unidade)',
        'Carnes de Porco (1 fatia)',
        'Frango - filé, sobrecoxa, peito (1 unidade)',
        'Peixe fresco / Frutos do Mar (1 unidade)',
      ]},
      { id: 'oleos', titulo: 'Óleos', itens: [
        'Azeite (1 colher de sopa)',
        'Bacon e toucinho / banha (1/2 fatia)',
        'Frituras',
        'Manteiga / Margarina (1/2 colher de sopa)',
        'Maionese (1/2 colher de sopa)',
        'Óleos vegetais (1 colher de sopa)',
      ]},
      { id: 'cereais', titulo: 'Cereais e Leguminosas', itens: [
        'Arroz Branco / Integral (4 colheres de sopa)',
        'Aveia (4 colheres de sopa)',
        'Pão francês / Integral / Forma (1 unidade)',
        'Macarrão (3 colheres e 1/2 de sopa)',
        'Bolos caseiros (1 fatia pequena)',
        'Leguminosas (1 concha)',
        'Soja (1 colher de servir)',
        'Oleaginosas (castanha/nozes/amendoim) (1 colher de sopa)',
      ]},
      { id: 'frutas_verduras', titulo: 'Frutas / Verduras / Legumes', itens: [
        'Fruta in natura (1 unidade/fatia)',
        'Folhosos (10 folhas)',
        'Tubérculos (batatas/cenoura/beterraba) (2 colheres de sopa)',
        'Legumes (abobora/chuchu/tomate/pepino) (2 colheres de sopa)',
      ]},
      { id: 'petiscos', titulo: 'Petiscos, Embutidos e Enlatados', itens: [
        'Snacks - salgadinhos, bolachas, pizza, amendoim (1 pacote)',
        'Macarrão instantâneo / lazanha / Nuggets (1 pacote)',
        'Embutidos em geral (presunto, mortadela etc) (2 fatias)',
        'Enlatados (milho, ervilha, palmito, azeitona) (2 colheres de sopa)',
      ]},
      { id: 'doces', titulo: 'Sobremesas e Doces', itens: [
        'Sorvete (1 unidade ou 2 bolas)',
        'Tortas e Doces Elaborados (1 fatia)',
        'Chocolates (1 unidade)',
        'Balas (1 unidade)',
      ]},
      { id: 'bebidas', titulo: 'Bebidas', itens: [
        'Água (1 garrafa 510 ml)',
        'Café sem açúcar (1 xícara café)',
        'Suco Natural / Chás sem açúcar (copo de requeijão)',
        'Refrigerante normal (copo de requeijao)',
        'Café / Chá com açúcar (1 xícara café)',
        'Suco Natural Adoçado (copo de requeijao)',
        'Sucos de Caixinha (copo de requeijao)',
      ]},
    ],
  },
};

export const TIPOS = Object.keys(QUESTIONARIOS);

// Total de itens respondíveis de um questionário
export function totalItens(tipo) {
  const q = QUESTIONARIOS[tipo];
  if (!q) return 0;
  if (q.secoes) return q.secoes.reduce((acc, s) => acc + s.itens.length, 0);
  return q.perguntas.length;
}

// Interpretação padrão da pontuação do rastreamento metabólico
export function interpretacaoRastreamento(total) {
  if (total < 30) return { label: 'Saudável', color: '#2E7D32', bg: 'rgba(76,175,80,0.1)' };
  if (total <= 40) return { label: 'Indicação leve', color: '#C8922A', bg: 'rgba(200,146,42,0.1)' };
  if (total <= 100) return { label: 'Indicação moderada', color: '#E07830', bg: 'rgba(224,120,48,0.1)' };
  return { label: 'Indicação severa', color: '#E53935', bg: 'rgba(229,57,53,0.1)' };
}
